import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import MenuBar from "../common/MenuPanel";
import FooterPanel from "../common/FooterPanel";
import { fetchMedia } from "../../services/AdminServices";

function LazyImage({ src, alt, className, loadingAttr = "lazy" }) {
  const imgRef = useRef();
  const [currentSrc, setCurrentSrc] = useState(null);
  useEffect(() => {
    if (!imgRef.current) return;
    if ("IntersectionObserver" in window) {
      const obs = new IntersectionObserver(
        (entries, observer) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setCurrentSrc(src);
              observer.unobserve(entry.target);
            }
          });
        },
        { rootMargin: "200px" } // preload a bit before visible
      );
      obs.observe(imgRef.current);
      return () => obs.disconnect();
    } else {
      // fallback
      setCurrentSrc(src);
    }
  }, [src]);

  // tiny transparent placeholder until loaded
  const placeholder =
    "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw==";

  return (
    <img
      ref={imgRef}
      src={currentSrc || placeholder}
      data-src={src}
      alt={alt}
      className={className}
      loading={loadingAttr}
    />
  );
}

function GalleryPage() {
  const { t, i18n } = useTranslation("gallery");
  const [items, setItems] = useState([]);
  const [lightboxItem, setLightboxItem] = useState(null);

  // batching parameters to keep few items in DOM at a time
  const BATCH_SIZE = 20;
  const [photosVisible, setPhotosVisible] = useState(BATCH_SIZE);
  const [videosVisible, setVideosVisible] = useState(BATCH_SIZE);
  const photosLoadRef = useRef(null);
  const videosLoadRef = useRef(null);

  useEffect(() => {
    async function loadMedia() {
      try {
        const data = await fetchMedia();
        setItems(data || []);
        console.log("useEffect Gallery items:", data);
      } catch (err) {
        console.error("Failed to fetch media:", err);
      }
    }
    loadMedia();
  }, []);

  // update page title / re-run when language changes
  useEffect(() => {
    document.title = t("gallery.title", "Gallery");
  }, [t, i18n.language]);

  const photos = items.filter((item) => item.type === "photo");
  const videos = items.filter((item) => item.type === "video");

  // observer to incrementally load more photos
  useEffect(() => {
    if (!photosLoadRef.current) return;
    if ("IntersectionObserver" in window) {
      const obs = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setPhotosVisible((v) => Math.min(photos.length, v + BATCH_SIZE));
            }
          });
        },
        { rootMargin: "200px" }
      );
      obs.observe(photosLoadRef.current);
      return () => obs.disconnect();
    }
  }, [photos.length]);

  // observer to incrementally load more videos
  useEffect(() => {
    if (!videosLoadRef.current) return;
    if ("IntersectionObserver" in window) {
      const obs = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setVideosVisible((v) => Math.min(videos.length, v + BATCH_SIZE));
            }
          });
        },
        { rootMargin: "200px" }
      );
      obs.observe(videosLoadRef.current);
      return () => obs.disconnect();
    }
  }, [videos.length]);

  return (
    <React.Fragment>
      <div className="container-fluid position-relative p-0">
        <MenuBar />
      </div>

      <section className="container-fluid p-0">
        <div
          className="banner-image_sm"
          style={{
            backgroundImage: `url(${process.env.PUBLIC_URL}/images/2.png)`,
          }}
        >
          <div className="banner-heading text-center wow fadeInDown">
            <h1 className="text-white">{t("gallery.title")}</h1>
          </div>
        </div>
      </section>

      <div className="gallery-page">
        <section className="gallery-section photos-section">
          <h2 className="section-title">{t("gallery.photos")}</h2>
          <div className="grid">
            {photos.slice(0, photosVisible).map((item) => (
              <div
                key={item.id}
                className="grid-item"
                onClick={() => setLightboxItem(item)}
              >
                <LazyImage
                  src={item.urlThumbnail}
                  alt={item.title || ""}
                  loadingAttr="lazy"
                />
                <div className="caption">{item.title}</div>
              </div>
            ))}
            {/* sentinel to load more */}
            {photosVisible < photos.length && (
              <div
                ref={photosLoadRef}
                style={{ width: "100%", height: 1, visibility: "hidden" }}
              />
            )}
          </div>
        </section>

        <section className="gallery-section videos-section">
          <h2 className="section-title">{t("gallery.videos")}</h2>
          <div className="grid">
            {videos.slice(0, videosVisible).map((item) => (
              <div
                key={item.id}
                className="grid-item"
                onClick={() => setLightboxItem(item)}
              >
                <LazyImage
                  src={item.urlThumbnail}
                  alt={item.title || ""}
                  loadingAttr="lazy"
                />
                <div className="caption">{item.title}</div>
              </div>
            ))}
            {videosVisible < videos.length && (
              <div
                ref={videosLoadRef}
                style={{ width: "100%", height: 1, visibility: "hidden" }}
              />
            )}
          </div>
        </section>

        {lightboxItem && (
          <div
            className="lightbox-overlay"
            onClick={() => setLightboxItem(null)}
          >
            <div
              className="lightbox-content"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="close-button"
                onClick={() => setLightboxItem(null)}
              >
                ×
              </button>
              {lightboxItem.type === "photo" ? (
                <img
                  src={lightboxItem.urlMedia}
                  alt={lightboxItem.title || ""}
                />
              ) : (
                // only mount video element when opened
                <video src={lightboxItem.urlMedia} controls autoPlay />
              )}
              <div className="desc">
                <h2>{lightboxItem.title}</h2>
                <p>{lightboxItem.description}</p>
                {lightboxItem.eventDate && (
                  <small>
                    {new Date(lightboxItem.eventDate).toLocaleDateString()}
                  </small>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <FooterPanel />
    </React.Fragment>
  );
}

export default GalleryPage;
